/**
 * Created by Jerker Nord on 2022-03-31.
 */

import {MotionBlurFilter} from "pixi-filters";
import {gsap} from "gsap";
import {NolimitPlugin} from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import {TextureHelper} from "../utils/TextureHelper";
import {TheBorder} from "../TheBorder";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export interface ITextureContainer {
    textureName:string,
    container:PIXI.Container,
    extraWidth:number,
    filters:PIXI.Filter[],
    forceWidth?:number,
    forceHeight?:number
}

export class ResourceCreator {

    private static SPIN_SYMBOL_NAMES:string[] = ["L1", "L2", "L3", "M1", "M2", "M3", "M4", "M5", "X_BOMB", "BN", "WR", "X_SPLIT", "X_SPLIT_SPENT", "X_WAYS",
        "L1_H", "L2_H", "L3_H", "M1_H", "M2_H", "M3_H", "M4_H", "M5_H", "X_BOMB_H", "BN_H", "WR_H", "X_SPLIT_H", "X_SPLIT_SPENT_H", "X_WAYS_H"];
    private static W_SPIN_SYMBOL_NAMES:string[] = ["L1", "L2", "L3", "M1", "M2", "M3", "M4", "M5", "X_BOMB"];
    private static verticalSpinBlurFilter:MotionBlurFilter = new MotionBlurFilter([0, 30], 35, 0);
    private static textureContainers:ITextureContainer[] = [];

    constructor() {

    }

    public static createResources():Promise<NolimitPlugin> {
        return new Promise<any>((resolve, reject) => {

            this.createSymbolSpinTextures();
            this.createWSymbolSpinTextures();
            this.createHalfSizeWireTextures();

            //We need to add some small delays not to choke up the browser
            const batchSize:number = 20;
            let delay:number = 0;
            const createCalls:gsap.core.Timeline = gsap.timeline({paused : true});
            const destroyCalls:gsap.core.Timeline = gsap.timeline({paused : true});
            while(ResourceCreator.textureContainers.length > 0) {
                delay += 0.01;
                let batch:ITextureContainer[] = [];
                if(ResourceCreator.textureContainers.length < batchSize) {
                    batch = ResourceCreator.textureContainers.splice(-ResourceCreator.textureContainers.length, ResourceCreator.textureContainers.length);
                } else {
                    batch = ResourceCreator.textureContainers.splice(-batchSize, batchSize);
                }

                createCalls.add(() => ResourceCreator.addBatchTextureContainersAsTextures(batch), delay);
                destroyCalls.add(() => ResourceCreator.addBatchDestroy(batch), delay);
            }

            const tl:gsap.core.Timeline = gsap.timeline({
                paused : true, onComplete : () => {
                    resolve(undefined);
                }
            });

            tl.add(createCalls.play(0));
            tl.add(destroyCalls.play(0));
            tl.play(0);
        });
    }

    private static createHalfSizeWireTextures():void {
        const wireTextures:PIXI.Texture[] = TheBorder.assetLoader.getTextures("Bwire/Bwire_");
        for(let i:number = 0; i < wireTextures.length; i++) {
            const container:PIXI.Container = new PIXI.Container();
            const barbSprite:PIXI.Sprite = new PIXI.Sprite(wireTextures[i]);
            container.addChild(barbSprite);

            let data:ITextureContainer = {
                container : container,
                textureName : "BwireHalf_00" + (i + 1),
                extraWidth : 0,
                filters : [],
                forceWidth : 60
            }

            ResourceCreator.addTextureContainer(data);
        }
    }

    private static createWSymbolSpinTextures():void {
        for(let i:number = 0; i < ResourceCreator.W_SPIN_SYMBOL_NAMES.length; i++) {
            const container:PIXI.Container = new PIXI.Container();
            const spinSymbolTexture:PIXI.Texture = TheBorder.assetLoader.getTextures(ResourceCreator.W_SPIN_SYMBOL_NAMES[i] + "/" + ResourceCreator.W_SPIN_SYMBOL_NAMES[i] + "_W")[0];
            const spinSymbol:PIXI.Sprite = new PIXI.Sprite(spinSymbolTexture);
            container.addChild(spinSymbol);

            let textureName:string = ResourceCreator.W_SPIN_SYMBOL_NAMES[i] + "/" + ResourceCreator.W_SPIN_SYMBOL_NAMES[i] + "_W" + "_Spin";
            if (ResourceCreator.W_SPIN_SYMBOL_NAMES[i] == "X_BOMB"){
                textureName = ResourceCreator.W_SPIN_SYMBOL_NAMES[i] + "/" + "XBomb" + "_W" + "_Spin";
            }

            ResourceCreator.addTextureContainer({
                container : container,
                textureName : textureName,
                extraWidth : 0,
                filters : [ResourceCreator.verticalSpinBlurFilter]
            })
        }
    }

    private static createSymbolSpinTextures():void {
        for(let i:number = 0; i < ResourceCreator.SPIN_SYMBOL_NAMES.length; i++) {
            const container:PIXI.Container = new PIXI.Container();
            const spinSymbolTexture:PIXI.Texture = TheBorder.assetLoader.getTextures(ResourceCreator.SPIN_SYMBOL_NAMES[i] + "/" + ResourceCreator.SPIN_SYMBOL_NAMES[i])[0];
            const spinSymbol:PIXI.Sprite = new PIXI.Sprite(spinSymbolTexture);
            container.addChild(spinSymbol);

            ResourceCreator.addTextureContainer({
                container : container,
                textureName : ResourceCreator.SPIN_SYMBOL_NAMES[i] + "/" + ResourceCreator.SPIN_SYMBOL_NAMES[i] + "_spin",
                extraWidth : 0,
                filters : [ResourceCreator.verticalSpinBlurFilter]
            })
        }
    }

    private static addTextureContainer(textureContainer:ITextureContainer):void {
        ResourceCreator.textureContainers.push(textureContainer);
    }

    private static addBatchTextureContainersAsTextures(batch:ITextureContainer[]):void {
        let containerTexture:PIXI.Texture;
        for(let i:number = 0; i < batch.length; i++) {
            batch[i].container.filters = batch[i].filters;
            containerTexture = TextureHelper.createRenderedTextureFromContainer(batch[i].container, batch[i].extraWidth, 0, NolimitApplication.resolution, batch[i].forceWidth, batch[i].forceHeight);
            PIXI.Texture.addToCache(containerTexture, batch[i].textureName);
        }
    }

    private static addBatchDestroy(batch:ITextureContainer[]):void {
        for(let i:number = 0; i < batch.length; i++) {
            batch[i].container.destroy({children : true});
        }
    }
}